import { Notifier } from '@airbrake/browser';
import type { Site } from '../types';
import { AIRBRAKE_PROJECT_ID, AIRBRAKE_PROJECT_KEY, IGNORED_USER_AGENTS } from './airbrakeConfig';
import {
  EXPLICIT_REPORT_PARAM,
  errorEventIsFromV2Bundle,
  isExplicitReport,
  isIgnoredUserAgent,
  rejectionIsFromV2Bundle,
  shouldCreateNotifier,
} from './gate';

let notifier: Notifier | null = null;

/** Only layouts/v2/base.html puts v2-root on <html>; legacy pages carry it on their mounts alone. */
export function isV2LayoutPage(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('v2-root');
}

/**
 * Constructing a Notifier registers online/offline and DOM breadcrumb listeners whatever its
 * options say, so legacy pages must never reach the constructor. Its own onerror and
 * unhandledrejection hooks stay off: page-wide errors go through the listeners below, which
 * report only what proves it came from the V2 bundle. Visitors in IGNORED_USER_AGENTS get no
 * notifier at all, so nothing they hit is reported.
 */
export function initErrorReporting(site: Site): void {
  if (
    typeof window === 'undefined' ||
    notifier ||
    !shouldCreateNotifier(site.environment, isV2LayoutPage()) ||
    isIgnoredUserAgent(window.navigator?.userAgent, IGNORED_USER_AGENTS)
  ) {
    return;
  }

  notifier = new Notifier({
    projectId: AIRBRAKE_PROJECT_ID,
    projectKey: AIRBRAKE_PROJECT_KEY,
    environment: 'production',
    instrumentation: {
      onerror: false,
      unhandledrejection: false,
      fetch: false,
      history: false,
      console: false,
      xhr: false,
    },
    performanceStats: false,
    queryStats: false,
    queueStats: false,
    remoteConfig: false,
  });

  notifier.addFilter((notice) => {
    if (!isExplicitReport(notice)) {
      return null;
    }

    notice.context = {
      ...notice.context,
      url: window.location.origin + window.location.pathname,
      siteId: site.id,
      brand: site.brand,
    };

    return notice;
  });

  window.addEventListener('error', (event) => {
    try {
      if (errorEventIsFromV2Bundle(event)) {
        reportError(event.error ?? new Error(event.message), { source: 'window.error' });
      }
    } catch {
      // A listener that throws would raise another error event from inside this one.
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      if (rejectionIsFromV2Bundle(event.reason)) {
        reportError(event.reason, { source: 'unhandledrejection' });
      }
    } catch {
      // A listener that throws would raise another error event from inside this one.
    }
  });
}

export function reportError(error: unknown, context: Record<string, unknown> = {}): void {
  try {
    if (notifier) {
      notifier.notify({ error, params: { ...context, [EXPLICIT_REPORT_PARAM]: true } }).catch(() => undefined);
      return;
    }

    console.error('[v2 error]', error, context);
  } catch {
    // A reporter that throws would take down the code that was only trying to report.
  }
}
